import { BrandArtwork, type ColourwayId } from '@/components/brand/BrandArtwork';
import type { PersonalisationValues } from '@/types/catalogue';

/**
 * Live personalisation preview — BUILD-PLAN §16, §46.
 *
 * §16 asks for the customer to see their entry applied ("Customer enters:
 * Sarah → Preview"). This overlays the entered name, monogram and date onto
 * the product artwork as they type.
 *
 * Two honest limits, both deliberate:
 *
 *  • It is an *impression*, not a proof. The label says so. Presenting a
 *    generated mockup as an exact representation of hand engraving would be
 *    a promise the workshop cannot keep (§86).
 *  • The preview is cosmetic only. Whatever it shows, the server revalidates
 *    the underlying values before they reach an order (§10) — so a customer
 *    cannot get an unapproved value manufactured by editing the DOM.
 */

interface PersonalisationPreviewProps {
  productName: string;
  values: PersonalisationValues;
  colourway?: ColourwayId | undefined;
  /** Ribbon colour choice, when the product offers one. */
  ribbonHex?: string | undefined;
}

export function PersonalisationPreview({
  productName,
  values,
  colourway,
  ribbonHex,
}: PersonalisationPreviewProps) {
  const name = (values['recipient_name'] ?? '').trim();
  const monogram = (values['monogram'] ?? '').trim().toUpperCase();
  const date = (values['special_date'] ?? '').trim();

  const hasContent = Boolean(name || monogram || date);

  return (
    <figure className="flex flex-col gap-3">
      <div className="relative aspect-4/5 w-full overflow-hidden shadow-card">
        <BrandArtwork
          seed={productName}
          ratio="portrait"
          {...(colourway ? { colourway } : {})}
        />

        {/* Engraving plate. Sits over the artwork rather than inside it so the
            text can animate independently as the customer types. */}
        {hasContent && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 px-8 text-center">
              {monogram && (
                <span
                  className="font-display text-5xl leading-none text-white/95 drop-shadow-[0_1px_6px_rgba(0,0,0,0.45)]"
                  style={{ letterSpacing: '0.08em' }}
                >
                  {monogram}
                </span>
              )}

              {name && (
                <span
                  className="font-script text-4xl leading-tight drop-shadow-[0_1px_6px_rgba(0,0,0,0.45)]"
                  // Ribbon choice tints the engraving so the two selections
                  // visibly relate to one another.
                  style={{ color: ribbonHex ?? '#E8CE93' }}
                >
                  {name}
                </span>
              )}

              {(monogram || name) && date && (
                <span
                  aria-hidden="true"
                  className="my-1 h-px w-14"
                  style={{ backgroundColor: ribbonHex ?? '#E8CE93', opacity: 0.75 }}
                />
              )}

              {date && (
                <span className="text-[0.625rem] uppercase tracking-[0.35em] text-white/85">
                  {formatDate(date)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <figcaption className="text-xs leading-relaxed text-muted">
        {hasContent
          ? 'A preview of your personalisation. Final placement and lettering are finished by hand, so the piece you receive may differ slightly.'
          : 'Start typing to see your personalisation previewed here.'}
      </figcaption>
    </figure>
  );
}

/** Renders an ISO date in the brand's editorial style, e.g. "14 · 02 · 2026". */
function formatDate(iso: string): string {
  const parsed = new Date(iso);

  if (Number.isNaN(parsed.getTime())) {
    // Mid-typing the value is not yet a valid date; show it as entered rather
    // than flashing "Invalid Date".
    return iso;
  }

  const pad = (value: number) => String(value).padStart(2, '0');

  return `${pad(parsed.getDate())} · ${pad(parsed.getMonth() + 1)} · ${parsed.getFullYear()}`;
}
